import gsap from "gsap";
import { $ } from "./dom";

export interface PreloaderOptions {
  textureUrls: string[];
  loadTexture: (url: string) => Promise<unknown>;
}

/**
 * Branded preloader: the H & B serif mark fades in, a thin progress line below
 * fills as assets load, then the whole thing fades out. Minimum visible time
 * keeps it from being a flash on instant connections.
 */
export async function runPreloader({
  textureUrls,
  loadTexture,
}: PreloaderOptions): Promise<void> {
  const node = $("#preloader");
  const fill = $<HTMLElement>("[data-preloader-fill]");
  const mark = $<HTMLElement>(".preloader__mark");
  if (!node) return;

  const total = Math.max(textureUrls.length, 1);
  let done = 0;

  function setProgress(p: number) {
    if (fill) fill.style.transform = `scaleX(${Math.max(0, Math.min(1, p))})`;
  }

  setProgress(0);

  // Fade the brand mark in
  if (mark) {
    gsap.from(mark, { opacity: 0, y: 6, duration: 0.7, ease: "power2.out" });
  }

  const tasks = textureUrls.map((url) =>
    loadTexture(url).finally(() => {
      done += 1;
      setProgress(done / total);
    }),
  );

  await Promise.all([Promise.all(tasks), wait(1100)]);
  setProgress(1);
  await wait(220);

  await new Promise<void>((resolve) => {
    gsap.to(node, {
      opacity: 0,
      duration: 0.7,
      ease: "power2.out",
      onComplete: () => {
        node.classList.add("is-done");
        resolve();
      },
    });
  });
}

function wait(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
